import type { CardTemplate, CardTemplatePage } from "../types";
import { multimediaIssue, publicItem, type MultimediaPage } from "./multimedia";

export interface PendingAsset { file: File; operationId: string; url: string; resolved?: string }
export class TemplateAssets {
  readonly pending = new Map<string, PendingAsset>();
  constructor(private readonly urls: Pick<typeof URL, "createObjectURL" | "revokeObjectURL"> = URL) {}
  add(file: File): string {
    const url = this.urls.createObjectURL(file);
    this.pending.set(url, { file, url, operationId: crypto.randomUUID() }); return url;
  }
  async resolve(url: string, upload: (asset: PendingAsset) => Promise<string>): Promise<string> {
    const asset = this.pending.get(url);
    if (!asset) { if (url.startsWith("blob:") || url.startsWith("data:")) throw new Error("This local media is no longer available. Choose it again."); return url; }
    if (!asset.resolved) asset.resolved = await upload(asset);
    return asset.resolved;
  }
  releaseAddedSince(before: Set<string>, keep: Set<string> = new Set()): void {
    for (const url of this.pending.keys()) if (!before.has(url) && !keep.has(url)) { this.urls.revokeObjectURL(url); this.pending.delete(url); }
  }
  dispose(): void { for (const url of this.pending.keys()) this.urls.revokeObjectURL(url); this.pending.clear(); }
}
export interface PublicationDependencies {
  upload: (asset: PendingAsset) => Promise<string>;
  exportPage: (page: MultimediaPage) => Promise<File>;
  commit: (name: string, pages: CardTemplatePage[], existing: CardTemplate | undefined, operationId: string) => Promise<CardTemplate>;
}
interface Attempt { key: string; operationId: string; pages?: CardTemplatePage[]; fallbacks: Map<string, string> }
export class TemplatePublisher {
  private attempt?: Attempt;
  private running = false;
  constructor(readonly assets: TemplateAssets, private readonly dependencies: PublicationDependencies) {}
  async publish(name: string, pages: CardTemplatePage[], existing?: CardTemplate): Promise<CardTemplate> {
    if (this.running) throw new Error("Publication is already pending");
    if (!name.trim() || !pages.length) throw new Error("Enter a name and save at least one complete page");
    const key = JSON.stringify({ name, pages, id: existing?.id, revision: existing?.revision });
    if (this.attempt?.key !== key) this.attempt = { key, operationId: crypto.randomUUID(), fallbacks: new Map() };
    const attempt = this.attempt;
    this.running = true;
    try {
      if (!attempt.pages) {
        const resolved: CardTemplatePage[] = [];
        for (const raw of pages) {
          const old = existing?.pages.find((page) => page.uid === raw.uid);
          if (old && JSON.stringify(old) === JSON.stringify(raw)) { resolved.push(structuredClone(raw)); continue; }
          const page = structuredClone(raw);
          if (page.type === "image" && page.multimedia) {
            const multimedia = page as MultimediaPage;
            const issue = multimediaIssue(multimedia); if (issue) throw new Error(issue);
            multimedia.multimedia.items = multimedia.multimedia.items.map(publicItem);
            for (const item of multimedia.multimedia.items) for (const field of ["image", "giphyUrl", "giphyThumbnail", "video", "thumbnail", "audio"] as const) {
              if (item.content[field]) item.content[field] = await this.assets.resolve(item.content[field]!, this.dependencies.upload);
            }
            multimedia.multimedia.version = multimedia.multimedia.items.some((item) => ["audio", "video"].includes(item.type)) ? 2 : 1;
            let fallback = attempt.fallbacks.get(page.uid);
            if (!fallback) { fallback = this.assets.add(await this.dependencies.exportPage(raw as MultimediaPage)); attempt.fallbacks.set(page.uid, fallback); }
            multimedia.image = await this.assets.resolve(fallback, this.dependencies.upload);
          } else {
            for (const field of ["image", "giphyUrl", "giphyThumbnail", "video", "thumbnail", "audio"]) {
              const record = page as unknown as Record<string, unknown>;
              if (typeof record[field] === "string") record[field] = await this.assets.resolve(record[field] as string, this.dependencies.upload);
            }
          }
          if (/"(?:blob:|data:)/.test(JSON.stringify(page))) throw new Error("Resolve all local media before publication");
          resolved.push(page);
        }
        attempt.pages = resolved;
      }
      return await this.dependencies.commit(name.trim(), attempt.pages, existing, attempt.operationId);
    } finally { this.running = false; }
  }
}
